/**
 * Utilidad para sincronizar filtros entre query params de URL y Vuex store
 */

/**
 * Parámetros de filtro que se mapean directamente sin necesidad de searchBy
 */
const DIRECT_FILTER_PARAMS = ['sort', 'order', 'limit', 'init', 'final', 'expand', 'dateRange']

/**
 * Parámetros que deben ser convertidos a números
 */
const NUMERIC_PARAMS = ['limit']

/**
 * Parámetros que deben ser convertidos a booleanos
 */
const BOOLEAN_PARAMS = ['expand']

/**
 * Convierte query params de la URL a un objeto de filtros para el store
 * @param {Object} queryParams - Los query params de $route.query
 * @param {Array} searchByFields - Lista de campos por los que se puede buscar (del store)
 * @returns {Object} - Objeto con los filtros a aplicar
 */
export function queryParamsToFilters (queryParams, searchByFields = []) {
  const filters = {}

  // Primero, procesar parámetros directos (sort, order, limit, etc.)
  DIRECT_FILTER_PARAMS.forEach(param => {
    if (queryParams[param] !== undefined) {
      // Convertir a número si es necesario
      if (NUMERIC_PARAMS.includes(param)) {
        filters[param] = parseInt(queryParams[param], 10)
      } else if (BOOLEAN_PARAMS.includes(param)) {
        filters[param] = queryParams[param] === 'true'
      } else {
        filters[param] = queryParams[param]
      }
    }
  })

  // Procesar parámetros de búsqueda
  // Identificar qué parámetros son campos de búsqueda válidos
  const searchParams = []
  Object.keys(queryParams).forEach(key => {
    const keyLowerCase = key.toLowerCase()
    
    // Si el parámetro es "Order" (mayúscula), se trata como búsqueda por el campo "order"
    if (key === 'Order') {
      searchParams.push({ field: 'order', value: queryParams[key] })
    } else if (!DIRECT_FILTER_PARAMS.includes(key) && searchByFields.includes(key)) {
      searchParams.push({ field: key, value: queryParams[key] })
    }
  })

  // Asignar a search/searchBy y search1/searchBy1
  if (searchParams.length > 0) {
    filters.search = searchParams[0].value
    filters.searchBy = searchParams[0].field
  }

  if (searchParams.length > 1) {
    filters.search1 = searchParams[1].value
    filters.searchBy1 = searchParams[1].field
  }

  // Si hay más de 2 parámetros de búsqueda, solo usamos los primeros 2
  // (el sistema actual solo soporta 2 búsquedas simultáneas)

  return filters
}

/**
 * Convierte el estado de filtros del store a query params para la URL
 * @param {Object} filters - El estado de filtros del store
 * @returns {Object} - Query params para router.push/replace
 */
export function filtersToQueryParams (filters) {
  const queryParams = {}

  // Agregar parámetros directos si tienen valor
  DIRECT_FILTER_PARAMS.forEach(param => {
    if (filters[param] !== undefined && filters[param] !== '') {
      queryParams[param] = filters[param]
    }
  })

  // Agregar búsquedas como parámetros nombrados
  if (filters.search && filters.searchBy) {
    // Si se busca por "order", usar "Order" (mayúscula) para evitar conflicto con parámetro de ordenamiento
    const searchByKey = filters.searchBy === 'order' ? 'Order' : filters.searchBy
    queryParams[searchByKey] = filters.search
  }

  if (filters.search1 && filters.searchBy1) {
    // Si se busca por "order", usar "Order" (mayúscula) para evitar conflicto con parámetro de ordenamiento
    const searchBy1Key = filters.searchBy1 === 'order' ? 'Order' : filters.searchBy1
    queryParams[searchBy1Key] = filters.search1
  }

  return queryParams
}

/**
 * Compara dos objetos de query params para ver si son diferentes
 * Útil para evitar actualizaciones innecesarias de la URL
 * @param {Object} params1
 * @param {Object} params2
 * @returns {boolean} - true si son diferentes
 */
export function areQueryParamsDifferent (params1, params2) {
  const keys1 = Object.keys(params1 || {})
  const keys2 = Object.keys(params2 || {})

  if (keys1.length !== keys2.length) return true

  return keys1.some(key => params1[key] !== params2[key])
}

/**
 * Aplica filtros desde query params al store
 * @param {Object} store - Instancia del store de Vuex
 * @param {String} resolver - El resolver/colección (ej: 'orders', 'deliveries')
 * @param {Object} queryParams - Los query params de la URL
 * @param {Array} searchByFields - Lista de campos por los que se puede buscar
 */
export function applyQueryParamsToStore (store, resolver, queryParams, searchByFields) {
  const filters = queryParamsToFilters(queryParams, searchByFields)

  Object.keys(filters).forEach(key => {
    store.commit('setConfigPage', {
      resolver,
      key,
      value: filters[key]
    })
  })
}

export default {
  queryParamsToFilters,
  filtersToQueryParams,
  areQueryParamsDifferent,
  applyQueryParamsToStore
}



// WEBPACK FOOTER //
// ./src/utils/urlFilters.js